import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { GooglePlaces, ListItem } from '@maximizer/core/shared/domain';
import { LocaleService } from './locale.service';

@Injectable()
export class GooglePlaceService {
  private placesLibPromise!: Promise<any>;
  private readonly locale: string = 'en-US';
  isApiKeyValidated = false;

  constructor(private readonly localeService: LocaleService) {
    this.locale = this.localeService.localeId ?? 'en-US';
    this.init();
  }

  private init(): void {
    this.placesLibPromise = this.loadPlacesLibrary();
  }

  public async validateApiKey(): Promise<boolean> {
    if (this.isApiKeyValidated) {
      return true;
    }
    try {
      const lib = await this.placesLibPromise;
      if (lib?.AutocompleteSessionToken) {
        this.isApiKeyValidated = true;
        return true;
      }
    } catch {
      this.isApiKeyValidated = false;
    }
    return false;
  }

  private async loadPlacesLibrary(): Promise<any> {
    if (!(window as any).google?.maps?.importLibrary) {
      throw new Error(
        'Google Maps JS API not loaded. Be sure to include it in index.html!',
      );
    }
    return await (window as any).google.maps.importLibrary('places');
  }

  search(searchText: string): Observable<ListItem<string>[]> {
    return from(
      this.placesLibPromise.then(async ({ AutocompleteSuggestion }) => {
        const request = {
          input: searchText,
          language: this.locale,
        };
        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        type Suggestion = {
          placePrediction: {
            placeId: string;
            text?: { toString(): string };
          };
        };

        return suggestions.map((suggestion: Suggestion) => ({
          id: suggestion.placePrediction.placeId,
          name: suggestion.placePrediction.text?.toString() ?? '',
        }));
      }),
    );
  }

  getPlaceDetails(
    placeId: string,
  ): Observable<GooglePlaces.GoogleAddress | null> {
    return from(
      this.placesLibPromise.then(async ({ Place }) => {
        const place = new Place({
          id: placeId,
          requestedLanguage: this.locale,
        });
        if (!place) return null;
        await place.fetchFields({
          fields: ['id', 'location', 'addressComponents'],
        });
        const mapper = new GooglePlaces.GooglePlacesMapper();
        return mapper.from(place);
      }),
    );
  }
}
